import React from "react";
import PaginationTable from "../../components/paginationTable/PaginationTable";
import {
  Comparator,
  dateFilter,
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import CamplaCoreService from "../../services/CamplaCoreService";
import Button from "react-bootstrap/Button";
import StorageService from "../../services/StorageService";
import {
  ButtonGroup,
  Col,
  Container,
  Row,
  ToggleButton,
} from "react-bootstrap";
import { Translation, withTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import APIErrorHelper from "../../helpers/APIErrorHelper";
import NetworkProfileActionCell from "./networkProfileActionCell/NetworkProfileActionCell";
import CreateNetworkProfileDialog from "./dialogs/createNetworkProfileDialog/CreateNetworkProfileDialog";
import UnsavedChangesDialog from "../../components/dialogs/unsavedChangesDialog/UnsavedChangesDialog";
import { Link } from "react-router-dom";
import DeleteDialog from "../../components/dialogs/deleteDialog/DeleteDialog";
import DateTimeHelper from "../../helpers/DateTimeHelper";

class NetworkProfiles extends React.Component {
  tableId = "networkProfilesTable";

  constructor(props) {
    super(props);
    let showFilters = StorageService.getTableConfigValue(
      this.tableId,
      "showFilters"
    );
    this.state = {
      networkProfiles: [],
      cloudProfiles: [],
      showCreateModal: false,
      showDeleteModal: false,
      showUpdateModal: false,
      showUnsavedChangesDialog: false,
      closeAction: () => {},
      networkProfileToDeleteOrEdit: null,
      errorMessage: "",
      columns: [],
      displayFilter: showFilters ? "initial" : "none",
      showFilters,
      dialogApiError: "",
      apiError: "",
    };
    this.loadData = this.loadData.bind(this);
    this.loadCachedData = this.loadCachedData.bind(this);
    this.handleCreateNetworkProfile = this.handleCreateNetworkProfile.bind(
      this
    );
    this.handleDeleteNetworkProfile = this.handleDeleteNetworkProfile.bind(
      this
    );
    this.onDelete = this.onDelete.bind(this);
  }

  async componentDidMount() {
    this.paginationTableRef = React.createRef();
    this.loadCachedData();
    this.setState(await this.loadData());
  }

  async handleCreateNetworkProfile(networkProfileToCreate) {
    try {
      const createdNetworkProfile = await CamplaCoreService.createNetworkProfile(
        networkProfileToCreate
      );
      const updatedNetworkProfiles = [
        ...this.state.networkProfiles,
        createdNetworkProfile,
      ];
      StorageService.set("networkProfiles", updatedNetworkProfiles);
      this.setState({
        showCreateModal: false,
        networkProfiles: updatedNetworkProfiles,
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED: "NetworkProfiles.APIError.PreconditionFailed",
            CONFLICT: "NetworkProfiles.APIError.CreateConflict",
          },
          this.props.t
        ),
      });
    }
  }

  async handleDeleteNetworkProfile() {
    try {
      await CamplaCoreService.deleteNetworkProfile(
        this.state.networkProfileToDeleteOrEdit._id
      );
      const updatedNetworkProfiles = this.state.networkProfiles.filter(
        (x) => x._id !== this.state.networkProfileToDeleteOrEdit._id
      );
      StorageService.set("networkProfiles", updatedNetworkProfiles);
      this.setState({
        showDeleteModal: false,
        networkProfiles: updatedNetworkProfiles,
        networkProfileToDeleteOrEdit: null,
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED: "NetworkProfiles.APIError.PreconditionFailed",
            NOT_FOUND: "NetworkProfiles.APIError.NotFound",
            CONFLICT: "NetworkProfiles.APIError.DeleteConflict",
          },
          this.props.t
        ),
      });
    }
  }

  loadCachedData() {
    const networkProfiles = StorageService.getReferenceType("networkProfiles");
    const cloudProfiles = StorageService.getReferenceType("cloudProfiles");
    if (networkProfiles) {
      this.setState({
        networkProfiles,
      });
    }
    if (cloudProfiles) {
      this.setState({
        cloudProfiles,
      });
    }
  }

  async loadData() {
    try {
      const networkProfiles = await CamplaCoreService.getAllNetworkProfiles();
      const cloudProfiles = await CamplaCoreService.getAllCloudNetworkProfiles();
      StorageService.set("networkProfiles", networkProfiles);
      StorageService.set("cloudProfiles", cloudProfiles);

      return {
        networkProfiles,
        cloudProfiles,
      };
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        ),
      });
    }
    return {
      networkProfiles: [],
      cloudProfiles: [],
    };
  }

  render() {
    const { t } = this.props;

    const columns = [
      {
        dataField: "name",
        text: t("NetworkProfiles.ColumnName"),
        sort: true,
        filter: textFilter({
          style: { display: this.state.displayFilter },
          // placeholder: ' '
        }),
        formatter: (name, networkProfile) => (
          <Link
            className="item-link"
            to={{
              pathname: `/networkProfiles/${networkProfile._id}`,
              state: {
                networkProfile,
              },
            }}
          >
            {name}
          </Link>
        ),
      },
      {
        dataField: "isPublished",
        text: t("NetworkProfiles.ColumnPublished"),
        formatter: this.getIsPublishedValue,
        sort: true,
        filter: selectFilter({
          options: [
            { value: true, label: t("NetworkProfiles.IsPublished") },
            { value: false, label: t("NetworkProfiles.IsNotPublished") },
          ],
          style: { display: this.state.displayFilter },
          placeholder: t("InstanceTemplates.FilterIsPublishedPlaceholder"),
        }),
      },
      {
        dataField: "updated_at",
        text: t("NetworkProfiles.ColumnUpdatedAt"),
        sort: true,
        formatter: (cell) => DateTimeHelper.formatDate(cell),
        filter: dateFilter({
          style: { display: this.state.displayFilter },
          defaultValue: { date: undefined, comparator: Comparator.EQ },
        }),
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
          <ButtonGroup toggle style={{ float: "right", marginRight: "-12px" }}>
            <ToggleButton
              type="checkbox"
              value={1}
              className="btn-green btn-sm"
              checked={this.state.showFilters}
              onChange={(e) => {
                this.setState({
                  showFilters: e.currentTarget.checked,
                  displayFilter: e.currentTarget.checked ? "initial" : "none",
                });
                StorageService.setTableConfigValue(
                  this.tableId,
                  "showFilters",
                  e.currentTarget.checked
                );
              }}
            >
              <FontAwesomeIcon icon={faFilter} />
            </ToggleButton>
          </ButtonGroup>
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatter: (cell, row) => {
          return (
            <NetworkProfileActionCell onDelete={() => this.onDelete(row)} />
          );
        },
      },
    ];

    return (
      <div>
        <CreateNetworkProfileDialog
          show={this.state.showCreateModal}
          cloudProfiles={this.state.cloudProfiles}
          onHide={(isFormDirty) => {
            if (isFormDirty) {
              this.setState({
                dialogApiError: null,
                showUnsavedChangesDialog: true,
                closeAction: () =>
                  this.setState({
                    showCreateModal: false,
                  }),
              });
            } else {
              this.setState({
                dialogApiError: null,
                showCreateModal: false,
              });
            }
          }}
          onSubmit={this.handleCreateNetworkProfile}
          dialogApiError={this.state.dialogApiError}
        />
        <UnsavedChangesDialog
          show={this.state.showUnsavedChangesDialog}
          onHide={() => this.setState({ showUnsavedChangesDialog: false })}
          onSubmit={() => {
            this.setState({ showUnsavedChangesDialog: false });
            this.state.closeAction();
          }}
        />
        <DeleteDialog
          show={this.state.showDeleteModal}
          onHide={() => this.setState({ showDeleteModal: false })}
          onSubmit={this.handleDeleteNetworkProfile}
          dialogApiError={this.state.dialogApiError}
          title={t("NetworkProfiles.DeleteModal.Title")}
          body={t("NetworkProfiles.DeleteModal.Text", {
            networkProfileName: this.state.networkProfileToDeleteOrEdit?.name,
          })}
          rightButtonText={t("NetworkProfiles.DeleteModal.Confirm")}
          leftButtonText={t("DialogCancel")}
        />

        <Container className="mt-3">
          <Row className="pageTitle">
            <Col>
              <h2>{t("NetworkProfiles.Title")}</h2>
            </Col>
            <Col>
              <Button
                className="btn-green"
                style={{ float: "right" }}
                onClick={() => this.setState({ showCreateModal: true })}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Col>
          </Row>
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row>
            <Col>
              <PaginationTable
                data={this.state.networkProfiles}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                ref={this.paginationTableRef}
                id={this.tableId}
                view="networkProfiles"
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  onDelete = (row) =>
    this.setState({
      showDeleteModal: true,
      networkProfileToDeleteOrEdit: row,
    });

  getIsPublishedValue = (isPublished) => (
    <Translation>
      {(t, { i18n }) => {
        if (isPublished) {
          return t("NetworkProfiles.IsPublished");
        }
        return t("NetworkProfiles.IsNotPublished");
      }}
    </Translation>
  );
}

export default withTranslation()(NetworkProfiles);
